import { useTitle } from "@/features/utils/use-title";
import {
  differenceInCalendarDays,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
} from "date-fns";
import { useState } from "react";
import {
  DateRangeSelector,
  type DateRangeOption,
} from "./components/date-range-selector";
import { LastCalls } from "./components/last-calls";
import { LastCustomers } from "./components/last-customers";
import { ActivityChart } from "./components/activity-chart";
import { Performance } from "./components/performance";
import { Classification } from "./components/classification";
import { AfterHours } from "./components/after-hours";
import { useSessionStats } from "@/features/sessions";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  StatCard,
  type StatBreakdownItem,
} from "@/components/molecules/stat-card";
import {
  ChatCircleDotsIcon,
  EnvelopeIcon,
  PhoneIcon,
} from "@phosphor-icons/react";
import { formatCurrency, formatDuration } from "@/features/utils/numbers";
import { Skeleton } from "@/components/ui/skeleton";
import { PageLayout } from "../layout";
import { useTranslation } from "@/features/utils/i18n";
import { useUser } from "@/features/auth/hooks/use-user";

const CHANNELS = [
  { id: "call", icon: PhoneIcon },
  { id: "chat", icon: ChatCircleDotsIcon },
  { id: "email", icon: EnvelopeIcon },
] as const;

export function AdminHomePage() {
  const { t } = useTranslation("dashboard");
  useTitle(t("title"));
  const [dateRange, setDateRange] = useState<DateRangeOption>("30d");

  return (
    <PageLayout
      title={t("title")}
      description={t("description")}
      actions={<DateRangeSelector value={dateRange} onChange={setDateRange} />}
    >
      <DashboardOverview dateRange={dateRange} />
    </PageLayout>
  );
}

/**
 * The dashboard itself, without its page: the client's own home, and the
 * "Dashboard" tab of the client's file in the platform back-office, which
 * mounts it under a read-only staff scope so both show the same figures.
 */
export function DashboardOverview({
  dateRange,
}: {
  dateRange: DateRangeOption;
}) {
  const { t, i18n } = useTranslation("dashboard");
  const { data: user } = useUser();

  // The first day of the range, at the viewer's local midnight. Everything on
  // the page counts days in the viewer's zone — the window sent to the server,
  // the per-day counts it returns and the chart's columns — so a bar is the
  // day it is labelled with.
  const getFrom = (range: DateRangeOption): Date => {
    const today = startOfDay(new Date());
    switch (range) {
      case "30d":
        return subDays(today, 30);
      case "60d":
        return subDays(today, 60);
      case "90d":
        return subDays(today, 90);
      case "mtd":
        return startOfMonth(today);
      case "ytd":
        return startOfYear(today);
    }
  };

  const from = getFrom(dateRange);
  // Today included.
  const days = differenceInCalendarDays(new Date(), from) + 1;

  const { data: stats, isLoading } = useSessionStats({
    date_from: from.toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // The user's own currency when they set one; the tenant has no setting of
  // its own, and every price in the product is entered in euros otherwise.
  const formatValue = (value: number) =>
    formatCurrency(value, i18n.language, user?.preferences?.currency, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  const totalMinutes =
    (stats?.total_minutes.call ?? 0) +
    (stats?.total_minutes.chat ?? 0) +
    (stats?.total_minutes.email ?? 0);

  const totalSessions =
    (stats?.total_sessions.call ?? 0) +
    (stats?.total_sessions.chat ?? 0) +
    (stats?.total_sessions.email ?? 0);

  const breakdown = (
    values: { call: number; chat: number; email: number } | undefined,
    format: (value: number) => string = (v) => v.toString(),
  ): StatBreakdownItem[] =>
    CHANNELS.map(({ id, icon }) => ({
      label: t(`channels.${id}`),
      icon,
      value: format(values?.[id] ?? 0),
    }));

  const chartSkeleton = (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[300px] w-full" />
      </CardContent>
    </Card>
  );

  return (
    <>
      {/* A regular grid: the four figures on one row, then two rows of a
          wide card beside a narrow one, each row as tall as its tallest. The
          last narrow column stacks the performance card over the after-hours
          one, beside the two recent lists. */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <StatCard
            loading={isLoading}
            label={t("metrics.totalDuration")}
            value={formatDuration(totalMinutes * 60)}
            breakdown={breakdown(stats?.total_minutes, (value) =>
              formatDuration(value * 60),
            )}
          />
          <StatCard
            loading={isLoading}
            label={t("metrics.avgDuration")}
            value={formatDuration(stats?.average_duration ?? 0)}
            breakdown={breakdown(
              stats?.average_duration_by_medium,
              formatDuration,
            )}
          />
          <StatCard
            loading={isLoading}
            label={t("metrics.sessions")}
            value={totalSessions}
            breakdown={breakdown(stats?.total_sessions)}
          />
          <StatCard
            loading={isLoading}
            label={t("metrics.totalValue")}
            value={formatValue(stats?.total_value ?? 0)}
            breakdown={breakdown(stats?.total_value_by_medium, formatValue)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="min-w-0 lg:col-span-2 [&>*]:h-full">
            {isLoading ? (
              chartSkeleton
            ) : (
              <ActivityChart
                data={stats?.sessions_by_day || []}
                days={days}
              />
            )}
          </div>
          <div className="min-w-0 [&>*]:h-full">
            {isLoading ? chartSkeleton : <Classification stats={stats} />}
          </div>

          <div className="min-w-0 space-y-4 lg:col-span-2">
            <LastCalls />
            <LastCustomers />
          </div>
          <div className="min-w-0 space-y-4">
            {isLoading ? (
              chartSkeleton
            ) : (
              <>
                <Performance stats={stats} />
                <AfterHours stats={stats} formatValue={formatValue} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
